import { EnrichmentOption } from "../enrichment/Enrichment.model";

import {
  ExploreAggColumnType,
  ExploreColumnType,
  ExploreGroupByColumnType,
} from "./columns/ExploreColum.model";
import {
  AllowedAggOrderBy,
  AllowedInteractionOrderBy,
} from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";

const exploreInteractionTypes = ["tickets", "conversations"] as const;
export type ExploreInteractionType = (typeof exploreInteractionTypes)[number];

export const exploreInteractionTypeToLabel: Record<
  ExploreInteractionType,
  { singular: string; plural: string }
> = {
  tickets: { singular: "Ticket", plural: "Tickets" },
  conversations: { singular: "Conversation", plural: "Conversations" },
};

export const resolutionStatuses = [
  "RESOLVED",
  "NOT_RESOLVED",
  "EXPECTED_ESCALATION",
  "UNEXPECTED_ESCALATION",
  "SKIPPED",
  "ERROR",
] as const;
export type ResolutionStatus = (typeof resolutionStatuses)[number];

export function isResolutionStatus(value: string): value is ResolutionStatus {
  return resolutionStatuses.includes(value as ResolutionStatus);
}

export const originAuthors = ["USER", "AGENT"];

export type OriginAuthor = (typeof originAuthors)[number];

export function isOriginAuthor(value: string): value is OriginAuthor {
  return originAuthors.includes(value as OriginAuthor);
}

export const escalationReasons = [
  "USER_REQUESTED",
  "BY_INSTRUCTIONS",
  "STRICT_MODE",
  "MAX_MESSAGES",
  "KNOWLEDGE_GAP",
  "LOW_CONFIDENCE",
  "OUT_OF_POLICY",
  "PICKUP_TIME_LIMIT",
  "UNCLASSIFIED",
  "ERROR",
] as const;
export type EscalationReason = (typeof escalationReasons)[number];

export function isEscalationReason(value: string): value is EscalationReason {
  return escalationReasons.includes(value as EscalationReason);
}

export const skipReasons = [
  "NO_RELEASE_FOUND",
  "TOPIC_NOT_RELEASED",
  "AUDIENCE_FILTER",
  "NOT_SUPPORTED_ATTACHMENTS",
  "OUT_OF_BUSINESS_HOURS",
  "EXCLUDED_FROM_QUACK",
  "UNCLASSIFIED",
  "ERROR",
  "ASSIGNED_TO_ANOTHER_AGENT",
] as const;
export type SkipReason = (typeof skipReasons)[number];

export function isSkipReason(value: string): value is SkipReason {
  return skipReasons.includes(value as SkipReason);
}

export const resolvedReasons = [
  "FULLY_RESOLVED",
  "ABANDONED",
  "ABANDONED_DURING_ESCALATION",
  "CLOSED_BY_USER",
  "UNCLASSIFIED",
  "ERROR",
] as const;
export type ResolvedReason = (typeof resolvedReasons)[number];

export function isResolvedReason(value: string): value is ResolvedReason {
  return resolvedReasons.includes(value as ResolvedReason);
}

export type Collaborator = {
  agentId: number;
  agentName?: string;
  avgQaScore: number;
  publicCommentsCount: number;
  internalCommentsCount: number;
};

export type Scorecard = {
  id: number;
  name: string;
  avgQaScore: string;
};

export type Team = {
  name: string;
  externalId: string;
};

export type SkillUsed = {
  id: string;
  name: string;
  slug: string;
};

export function isTeamArray(value: unknown): value is Team[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item != null &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        "externalId" in item
    )
  );
}

export interface ExploreInteractionRow {
  interactionType: ExploreInteractionType;
  tenantId?: string;
  id: string;
  displayId: string;
  subject: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  channel: string;
  topic: string;
  category: string;
  team?: Team[];
  avgQaScore?: number;
  avgAutoQaScore?: number;
  avgSentiment?: number;
  lastSentiment?: number;
  avgQuackMessageScore?: number;
  countPublicMessages: number;
  countInternalMessages: number;
  timeToResolution?: number;
  timeToFirstResponse?: number;
  countQuackMessages?: number;
  isQuackEscalated?: boolean;
  isQuackAnswered?: boolean;
  isAnsweredByAui?: boolean;
  collaborators?: Collaborator[];
  isFiniteState?: boolean;
  scorecards?: Scorecard[];
  resolutionStatus?: ResolutionStatus;
  escalationReason?: EscalationReason;
  skipReason?: SkipReason;
  tags?: string[];
  scoreTitle?: string;
  scoreContent?: string;
  csat?: number;
  csatComment?: string;
  skillUsed?: SkillUsed;
  skillsUsed?: SkillUsed[];

  // Ticket specific fields
  description?: string;
  subTopic?: string;
  agentAssigned?: string;
  agentAssignedId?: number;
  tenantUser?: string;
  tenantOrganization?: string;
  validationStatus?: boolean;
  originAuthor?: OriginAuthor;

  // Conversation specific fields
  ticketId?: string;
  resolvedReason?: ResolvedReason;
  originUrl?: string;
  isUserAuthenticated?: boolean;
}

export interface ExploreAggInteractionRow {
  volume: number;
  avgQuackMessages?: number;
  avgQaScore?: number;
  avgQaScoreRoundRange?: number;
  avgQuackMessageScoreRoundRange?: number;
  avgSentiment?: number;
  avgCsat?: number;
  avgTimeToResolution?: number;
  avgTimeToFirstResponse?: number;
  avgPublicMessages: number;
  avgInternalMessages: number;

  resolutionStatus?: ResolutionStatus;
  escalationReason?: EscalationReason;
  skipReason?: SkipReason;
  resolvedReason?: ResolvedReason;

  id?: string;
  tenantId?: string;
  status?: string;
  channel?: string;
  topic?: string;
  category?: string;
  subTopic?: string;
  agentAssigned?: string;
  team?: string;
  day?: string;
  week?: string;
  month?: string;
  isQuackAnswered?: boolean;
  isAnsweredByAui?: boolean;
  avgQaScoreByTitle?: number;
  avgQaScoreByContent?: number;
  scoreTitle?: string;
  scoreContent?: string;
  originUrl?: string;
  simulator?: string;
  skillUsed?: string;
}

export interface ExploreView {
  id: string;
  slug: string;
  title: string;
  source: "tickets" | "conversations" | "interactions";
  filterString?: string;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  groupBy?: ExploreGroupByColumnType;
  columns?: ExploreColumnType[];
  aggColumns?: ExploreAggColumnType[];
  aggOrderBy?: AllowedAggOrderBy | null;
  aggOrderDir?: "asc" | "desc" | null;
  createdAt: string;
  updatedAt: string;
  isSubscribed?: boolean;
}

export interface CreateExploreView {
  title: string;
  source: "tickets" | "conversations" | "interactions";
  filterString?: string | null;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  groupBy?: ExploreGroupByColumnType | null;
  columns?: ExploreColumnType[];
  aggColumns?: ExploreAggColumnType[] | null;
  aggOrderBy?: AllowedAggOrderBy | null;
  aggOrderDir?: "asc" | "desc" | null;
}

export function isKeyOfExploreAggInteractionRow(
  key: string,
  row: ExploreAggInteractionRow
): key is keyof ExploreAggInteractionRow {
  return key in row;
}

export interface ExploreEnrichment {
  id: string;
  value: string;
  label: string;
  options: EnrichmentOption[];
}

export interface ExploreCustomField {
  id: string;
  value: string;
  label: string;
  options: CustomFieldOption[];
}

export interface CustomFieldOption {
  id: string;
  name: string;
  value: string;
}
